import React, { useState, useEffect } from "react";
import logoService, { LogoServiceResponse } from "../Services/LogoService";

interface LogoManagerProps {
    onLogoChange: (hasLogo: boolean) => void;
}

export const LogoManager: React.FC<LogoManagerProps> = ({ onLogoChange }) => {
    const [logoUrl, setLogoUrl] = useState<string | null>(null);
    const [isUploading, setIsUploading] = useState(false);
    const [uploadError, setUploadError] = useState<string | null>(null);
    const [isLoading, setIsLoading] = useState(true);

    useEffect(() => {
        checkExistingLogo();

        // Cleanup when component unmounts
        return () => {
            logoService.cleanup();
        };
    }, []);

    const checkExistingLogo = async () => {
        setIsLoading(true);
        setUploadError(null);

        try {
            const result: LogoServiceResponse = await logoService.checkExistingLogo();

            if (result.hasLogo && result.logoUrl) {
                setLogoUrl(result.logoUrl);
                onLogoChange(true);
            } else {
                setLogoUrl(null);
                onLogoChange(false);

                if (result.error) {
                    setUploadError(result.error);
                }
            }
        } catch (error) {
            console.error("Unexpected error checking logo:", error);
            setLogoUrl(null);
            onLogoChange(false);
            setUploadError("An unexpected error occurred");
        } finally {
            setIsLoading(false);
        }
    };

    const handleFileUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0];
        if (!file) return;

        setIsUploading(true);
        setUploadError(null);

        try {
            const result = await logoService.uploadLogo(file);

            if (result.success) {
                // After successful upload, refresh the logo display
                await checkExistingLogo();
            } else {
                setUploadError(result.error || 'Upload failed');
            }
        } catch (error) {
            console.error("Unexpected error uploading logo:", error);
            setUploadError("An unexpected error occurred during upload");
        } finally {
            setIsUploading(false);
        }
    };

    const handleRemoveLogo = async () => {
        setUploadError(null);

        try {
            const result = await logoService.removeLogo();

            if (result.success) {
                setLogoUrl(null);
                onLogoChange(false);
            } else {
                setUploadError(result.error || 'Failed to remove logo');
            }
        } catch (error) {
            console.error("Unexpected error removing logo:", error);
            setUploadError("An unexpected error occurred while removing logo");
        }
    };

    if (isLoading) {
        return (
            <div className="flex items-center justify-center p-8 border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-lg">
                <div className="text-center">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
                    <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">Loading...</p>
                </div>
            </div>
        );
    }

    return (
        <div className="space-y-4">
            {/* Logo Display Area */}
            <div className="border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-lg p-6">
                {logoUrl ? (
                    <div className="text-center">
                        <div className="relative inline-block">
                            <img
                                src={logoUrl}
                                alt="Company Logo"
                                className="max-h-32 max-w-full object-contain rounded"
                                onError={() => {
                                    console.error("Failed to load logo image");
                                    setLogoUrl(null);
                                    onLogoChange(false);
                                    setUploadError("Failed to display logo image");
                                }}
                            />
                            <button
                                type="button"
                                onClick={handleRemoveLogo}
                                className="absolute -top-2 -right-2 bg-red-500 hover:bg-red-600 text-white rounded-full p-1 text-xs transition-colors"
                                title="Remove logo"
                            >
                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                </svg>
                            </button>
                        </div>
                        <p className="mt-2 text-sm text-gray-600 dark:text-gray-400">
                            Current logo
                        </p>
                    </div>
                ) : (
                    <div className="text-center">
                        <div className="mx-auto h-16 w-16 bg-gray-100 dark:bg-gray-700 rounded-lg flex items-center justify-center">
                            <svg className="h-8 w-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                            </svg>
                        </div>
                        <p className="mt-2 text-sm text-gray-600 dark:text-gray-400">
                            No logo uploaded
                        </p>
                        <p className="text-xs text-gray-500 dark:text-gray-500">
                            Upload a logo to use on your invoices
                        </p>
                    </div>
                )}
            </div>

            {/* Upload Controls */}
            <div className="flex flex-col sm:flex-row gap-3">
                <label className="flex-1">
                    <input
                        type="file"
                        accept="image/png,image/jpeg,image/jpg"
                        onChange={handleFileUpload}
                        disabled={isUploading}
                        className="sr-only"
                    />
                    <div className={`
                        cursor-pointer inline-flex items-center justify-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white
                        ${isUploading
                            ? 'bg-gray-400 cursor-not-allowed'
                            : 'bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500'
                        }
                        transition-colors w-full sm:w-auto
                    `}>
                        {isUploading ? (
                            <>
                                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                                Uploading...
                            </>
                        ) : (
                            <>
                                <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                                </svg>
                                {logoUrl ? 'Replace Logo' : 'Upload Logo'}
                            </>
                        )}
                    </div>
                </label>

                {logoUrl && (
                    <button
                        type="button"
                        onClick={handleRemoveLogo}
                        className="px-4 py-2 border border-gray-300 dark:border-gray-600 text-sm font-medium rounded-md text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors"
                    >
                        Remove Logo
                    </button>
                )}
            </div>

            {/* Error Message */}
            {uploadError && (
                <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-md p-3">
                    <div className="flex">
                        <div className="flex-shrink-0">
                            <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                            </svg>
                        </div>
                        <div className="ml-3">
                            <p className="text-sm text-red-800 dark:text-red-200">{uploadError}</p>
                        </div>
                    </div>
                </div>
            )}

            {/* Upload Guidelines */}
            <div className="text-xs text-gray-500 dark:text-gray-400 space-y-1">
                <p>• Supported formats: PNG, JPG</p>
                <p>• Maximum file size: 2MB</p>
                <p>• Recommended size: 300x100px or similar aspect ratio</p>
            </div>
        </div>
    );
};

